
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Urls } from '@share/constants';
import { AppThunk, axiosInstanceB2B, IsClientCashEnabled, Toaster } from '@share/utils';
import { getHeaders, axiosInstance } from '@share/utils';
import { get, isEmpty } from 'lodash';

export interface IPasswordState {
  error: string;
  isSuccess: boolean;
  loading: boolean;
  result: any;
}

const initialState: IPasswordState = {
  error: '',
  isSuccess: false,
  loading: false,
  result: {}
};

const passwordSlice = createSlice({
  name: 'password',
  initialState,
  reducers: {
    setSuccess: (state: IPasswordState, { payload }: PayloadAction<boolean>) => {
      state.isSuccess = payload;
    },
    setError: (state: IPasswordState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setLoading: (state: IPasswordState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setResult: (state: IPasswordState, { payload }: PayloadAction<any>) => {
      state.result = payload;
    }
  },
});

export const passwordSliceActions = passwordSlice.actions;
export const passwordReducer = passwordSlice.reducer;

export const passwordSelector = (state: { passwordStore: IPasswordState }): IPasswordState => {
  return state.passwordStore;
};

export const verifyUser = (token: string): AppThunk => {
  return async (dispatch) => {
    try {
      const response = await axiosInstance.post(`${Urls.Users}/verify`, { token }, { ...getHeaders() });
      const { statusCode, message, data } = response.data;
      dispatch(passwordSliceActions.setSuccess(statusCode === 200));
      dispatch(passwordSliceActions.setError(data));

      Toaster.success(message)
    } catch (error: any) {
      let errorMessage = error?.response?.data ? get(JSON.parse(error?.response?.data), 'message', null) : null;
      if (isEmpty(errorMessage)) {
        errorMessage = error.toString();
      }
      dispatch(passwordSliceActions.setSuccess(false));
      
      Toaster.error(errorMessage.toString())
    }
  };
};

export const sendVerifyEmail = (email: string): AppThunk => {
  return async (dispatch) => {
    try {
      const response = await axiosInstance.post(`${Urls.Users}/send-verify`, { email }, { ...getHeaders() });
      const { statusCode, message } = response.data;
      dispatch(passwordSliceActions.setSuccess(statusCode === 200));

      Toaster.success(message)
    } catch (error: any) {
      let errorMessage = error?.response?.data ? get(JSON.parse(error?.response?.data), 'message', null) : null;
      if (isEmpty(errorMessage)) {
        errorMessage = error.toString();
      }
      dispatch(passwordSliceActions.setSuccess(false));
      
      Toaster.error(errorMessage.toString())
    }
  };
};

export const forgot = (email: string): AppThunk => {
  return async (dispatch) => {
    try {
      const response = await axiosInstance.post(`${Urls.Users}/forgot`, { email }, { ...getHeaders() });
      const { statusCode, message } = response.data;
 
      dispatch(passwordSliceActions.setSuccess(statusCode === 200));
      Toaster.success(message);
    } catch (error: any) {
      let errorMessage: string | null = null;
 
      const errorData = error?.response?.data;
 
      if (typeof errorData === 'string') {
        try {
          debugger
          const parsed = JSON.parse(errorData);
          errorMessage = parsed?.reason || parsed?.message || null;
        } catch (parseErr) {
        }
      }
      if (typeof errorData === 'object') {
        errorMessage = errorData?.reason || errorData?.message || null;
      }
 
      if (!errorMessage) {
        errorMessage = "Something went wrong. Please try again.";
      }
 
      dispatch(passwordSliceActions.setSuccess(false));
      Toaster.error(errorMessage);
    }
  };
};

export const reset = (token: string, password: string, confirmPassword: string): AppThunk => {
  return async (dispatch) => {
    try {
      const response = await axiosInstance.post(`${Urls.Users}/reset`, { token, password, confirmPassword }, { ...getHeaders() });
      const { statusCode, message } = response.data;
      dispatch(passwordSliceActions.setSuccess(statusCode === 200));

      Toaster.success(message)
    } catch (error: any) {
      let errorMessage = error?.response?.data ? get(JSON.parse(error?.response?.data), 'message', null) : null;
      if (isEmpty(errorMessage)) {
        errorMessage = error.toString();
      }
      dispatch(passwordSliceActions.setSuccess(false));
      
      Toaster.error(errorMessage.toString())
    }
  };
};

export const change = (currentPassword: string, newPassword: string): AppThunk => {
  return async (dispatch, getState) => {
    try {
      dispatch(passwordSliceActions.setLoading(true));
      const { loginStore } = getState();
      const { account, user, userWallet } = loginStore;
      const isClientCashEnabled = IsClientCashEnabled(account);

      const userId = isClientCashEnabled ? userWallet?.userId : user?.userId;

      const response = await axiosInstance.post(`${Urls.Users}/change`, { userId, currentPassword, newPassword }, { ...getHeaders() });
      const { statusCode, message } = response.data;
      dispatch(passwordSliceActions.setSuccess(statusCode === 200));
      dispatch(passwordSliceActions.setLoading(false));

      Toaster.success(message)
    } catch (error: any) {
      let errorMessage = error?.response?.data ? get(JSON.parse(error?.response?.data), 'message', null) : null;
      if (isEmpty(errorMessage)) {
        errorMessage = error.toString();
      }
      dispatch(passwordSliceActions.setSuccess(false));
      dispatch(passwordSliceActions.setLoading(false));
      
      Toaster.error(errorMessage.toString())
    }
  };
};

export const resetPassword = (token: string, password: string, confirmPassword: string): AppThunk => {
  return async (dispatch) => {
    try {
      const response = await axiosInstanceB2B.post(`/identity/password/reset-password`, { token, password, confirmPassword });
      dispatch(passwordSliceActions.setResult(response.data.result));
      dispatch(passwordSliceActions.setSuccess(response.data.success));

      Toaster.success('Success')
    } catch (error: any) {
      dispatch(passwordSliceActions.setSuccess(false));

      Toaster.error('An error occurred. If the problem persists, please contact the system administrator.')
    }
  };
};